import React from "react";

import Modal from "../../../components/modal/Modal";
import Table, { TableColumn } from "../../../components/table/Table";
import InlineLoading from "../../../components/loading/InlineLoading";
import DateDisplay from "../../../components/date-display/DateDisplay";
import {
  useGetAdminGitHubWriteLogsQuery,
  type AdminGitHubRepository,
  type GitHubWriteLog,
} from "../../../app/services/github";

const ACTION_LABELS: Record<GitHubWriteLog["action"], string> = {
  create_branch: "ブランチ作成",
  commit: "コミット",
  create_pull_request: "PR 作成",
};

const COLUMNS: TableColumn[] = [
  { key: "created_at", header: "日時", width: 150, minWidth: 120 },
  { key: "user", header: "依頼者", width: 140, minWidth: 100 },
  { key: "action", header: "操作", width: 110, minWidth: 90 },
  { key: "target", header: "ブランチ / 内容", minWidth: 200 },
];

const summary = (log: GitHubWriteLog): string => {
  if (log.action === "commit") {
    const files = (log.detail.files as unknown[] | undefined)?.length ?? 0;
    return `${log.commit_sha.slice(0, 7)}（${files} ファイル）`;
  }
  if (log.action === "create_pull_request") {
    return `#${log.detail.number ?? ""}`;
  }
  return log.commit_sha ? `${log.commit_sha.slice(0, 7)} から作成` : "";
};

export interface GitHubWriteLogsModalProps {
  repository: AdminGitHubRepository;
  onClose: () => void;
}

/** soremon 経由で GitHub に書き込んだ記録（新しい順 100 件）。 */
const GitHubWriteLogsModal: React.FC<GitHubWriteLogsModalProps> = ({ repository, onClose }) => {
  const { data, isLoading, isError } = useGetAdminGitHubWriteLogsQuery(repository.id, {
    refetchOnMountOrArgChange: true,
  });
  const logs = data?.data?.logs ?? [];

  const renderRows = () => {
    if (isLoading || isError || logs.length === 0) {
      return (
        <tr>
          <td colSpan={COLUMNS.length} className="bodytext-13 u-text-light-gray u-text-center">
            {isLoading ? <InlineLoading visible /> : isError ? "記録の取得に失敗しました" : "記録はまだありません"}
          </td>
        </tr>
      );
    }
    return logs.map((log) => (
      <tr key={log.id}>
        <td className="bodytext-13">
          <DateDisplay date={log.created_at} />
        </td>
        <td className="bodytext-13">{log.user?.display_name ?? "—"}</td>
        <td className="bodytext-13">{ACTION_LABELS[log.action] ?? log.action}</td>
        <td className="bodytext-13">
          <span>{log.branch}</span>{" "}
          {log.url ? (
            <a href={log.url} target="_blank" rel="noreferrer" className="u-text-blue">
              {summary(log)}
            </a>
          ) : (
            <span className="u-text-light-gray">{summary(log)}</span>
          )}
        </td>
      </tr>
    ));
  };

  return (
    <Modal
      title={`${repository.full_name} の書き込み記録`}
      titleSize="medium"
      size="wide"
      onClose={onClose}
    >
      <Table columns={COLUMNS} minWidth={600} maxBodyHeight={480}>
        {renderRows()}
      </Table>
    </Modal>
  );
};

export default GitHubWriteLogsModal;
